/**
 * A local mount: an OPFS directory served by the bundled BrowserDAV engine
 * inside the service worker (a-local-opfs-mount-type-in-the-service-worker).
 * No tab, no network hop, no token — the origin's own storage, answered
 * locally. The mount plugin (sw.js) branches here before it looks for
 * credentials; everything layered on mounts (the htmx layer, the file
 * browser, a sync engine) comes along for free.
 *
 * One engine per mount id, rooted at `navigator.storage.getDirectory()` →
 * `mount.opfsPath` (created on first use). `getDirectory()` and
 * `createWritable()` are available to service workers on Chrome (proven
 * 2026-09-25); the other engines are recorded in the project doc as they
 * are run.
 */
import { localRequestFor } from './swMounts.js'

const engines = new Map() // mount id → { filesystem, opfsPath }

/** Open (creating as needed) the OPFS directory a local mount serves. */
export async function openLocalRoot(opfsPath, { storage = globalThis.navigator?.storage } = {}) {
    if (!storage?.getDirectory) throw new Error('OPFS is not available in this worker')
    let handle = await storage.getDirectory()
    for (const seg of opfsPath.split('/').filter(Boolean)) {
        handle = await handle.getDirectoryHandle(seg, { create: true })
    }
    return handle
}

/** The engine for a local mount, made on first use and kept. */
export async function localEngineFor(mount, { WebDAVFileSystem, storage } = {}) {
    const cached = engines.get(mount.id)
    if (cached && cached.opfsPath === mount.opfsPath) return cached.filesystem
    if (!WebDAVFileSystem) throw new Error('the BrowserDAV engine is not in this bundle')
    const filesystem = new WebDAVFileSystem()
    filesystem.fileSystemHandler.rootHandle = await openLocalRoot(mount.opfsPath, { storage })
    engines.set(mount.id, { filesystem, opfsPath: mount.opfsPath })
    return filesystem
}

/** Forget every engine (a mount table reload with a changed local mount). */
export function resetLocalEngines() {
    engines.clear()
}

const BODYLESS = new Set(['GET', 'HEAD', 'OPTIONS', 'DELETE', 'MKCOL', 'COPY', 'MOVE', 'UNLOCK'])

/**
 * Answer one fetch for a local mount. Never throws for a WebDAV outcome;
 * an engine failure is a 500 that names it.
 */
export async function serveLocalMount({ mount, request, origin, WebDAVFileSystem, storage, requestId }) {
    const url = new URL(request.url)
    let filesystem
    try {
        filesystem = await localEngineFor(mount, { WebDAVFileSystem, storage })
    } catch (e) {
        return new Response(`Local mount unavailable: ${e?.message || e}`, { status: 500 })
    }
    const method = request.method.toUpperCase()
    let rawBody = null
    let body = null
    let bodyStream = null
    if (!BODYLESS.has(method)) {
        // A PUT is handed to the engine as the request's own stream, so the
        // bytes go to OPFS as they arrive instead of the whole file sitting
        // in the worker's memory first (2026-10-19; before, arrayBuffer()).
        if (method === 'PUT') {
            if (request.body) bodyStream = request.body
            else rawBody = await request.arrayBuffer()
        } else body = await request.text()
    }
    const engineRequest = localRequestFor(mount, url, { method, headers: request.headers, rawBody, body, bodyStream, requestId, origin })
    let result
    try {
        result = await filesystem.handleRequest(engineRequest)
    } catch (e) {
        return new Response(`Local mount error: ${e?.message || e}`, { status: e?.status || 500 })
    }
    const status = result?.status || 500
    const headers = new Headers(result?.headers || {})
    // A local answer is never a shared-cache candidate; the browser cache
    // would only hide a write from the next read.
    if (!headers.has('Cache-Control')) headers.set('Cache-Control', 'no-store')
    headers.set('X-Acequia-Mount', 'local')
    let payload = result?.body
    // A directory listing names entries from the engine's root; give them
    // in the mount's namespace, as the remote branch rewrites upstream
    // paths to VFS paths (`/notes/a.md` → `/draft/notes/a.md`).
    if (headers.get('x-acequia-directory-json') && typeof payload === 'string') {
        try {
            const list = JSON.parse(payload)
            if (Array.isArray(list)) {
                payload = JSON.stringify(
                    list.map((e) => (typeof e?.filename === 'string' && e.filename.startsWith('/') ? { ...e, filename: mount.mountpoint + e.filename } : e))
                )
                headers.delete('content-length')
            }
        } catch {
            /* not JSON after all — pass through */
        }
    }
    if ([101, 103, 204, 205, 304].includes(status)) payload = null
    else if (payload == null) payload = ''
    else if (typeof payload !== 'string' && !(payload instanceof Blob) && !(payload instanceof ArrayBuffer) && !ArrayBuffer.isView(payload) && !(payload instanceof ReadableStream)) {
        payload = JSON.stringify(payload)
    }
    return new Response(payload, { status, headers })
}
